import * as THREE from "three";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";
import { extractToothCrownVertices } from "@/class/toothCrownContours";

import { Box, Octree, computeBoundingBox } from "@/class/Octree";
import { VoxelGrid } from "@/class/voxelGrid";
import { createScenario } from "@/class/agMapBuilder";

export default {
  methods: {
    drawDentalArch() {
      const teethCenters = this.scene.userData.teethCenters || {};
      const upperTeeth = [];
      const lowerTeeth = [];

      // Separa os pontos baseados no quadrante ao qual eles pertencem
      Object.keys(teethCenters).forEach((toothNumber) => {
        const toothInt = parseInt(toothNumber);
        if (toothInt >= 11 && toothInt <= 28) {
          upperTeeth.push({
            toothNumber: toothInt,
            point: teethCenters[toothNumber],
          });
        } else if (toothInt >= 31 && toothInt <= 48) {
          lowerTeeth.push({
            toothNumber: toothInt,
            point: teethCenters[toothNumber],
          });
        }
      });

      // Ordena os dentes superiores e inferiores
      upperTeeth.sort((a, b) => a.toothNumber - b.toothNumber);
      lowerTeeth.sort((a, b) => a.toothNumber - b.toothNumber);

      // Inverte a ordem dos dentes do lado esquerdo (11-18)
      const upperLeft = upperTeeth
        .filter((tooth) => tooth.toothNumber <= 18)
        .reverse();
      // Mantém a ordem dos dentes do lado direito (21-28)
      const upperRight = upperTeeth.filter(
        (tooth) => tooth.toothNumber >= 21 && tooth.toothNumber <= 28
      );
      // Combina os arrays para formar o arco superior completo
      const upperArch = [...upperLeft, ...upperRight].map(
        (tooth) => tooth.point
      );

      // Semelhante para os dentes inferiores
      const lowerLeft = lowerTeeth
        .filter((tooth) => tooth.toothNumber > 38)
        .reverse();
      const lowerRight = lowerTeeth.filter(
        (tooth) => tooth.toothNumber >= 31 && tooth.toothNumber <= 38
      );
      const lowerArch = [...lowerLeft, ...lowerRight].map(
        (tooth) => tooth.point
      );

      // Encontra o grupo de bounding boxes na cena que tem a propriedade isTeethBoxes definida como true
      const teethBoxesGroup = this.scene.getObjectByName("Teeth Boxes");

      // Se o grupo de bounding boxes for encontrado e estiver marcado corretamente como isTeethBoxes
      if (teethBoxesGroup && teethBoxesGroup.userData.isTeethBoxes) {
        // Adiciona as linhas do arco superior e inferior usando o grupo como parent
        this.addLinesBetweenPoints(upperArch, teethBoxesGroup, false, 0.25); // Arco superior
        this.addLinesBetweenPoints(lowerArch, teethBoxesGroup, false, 0.25); // Arco inferior
      } else {
        console.warn(
          "Grupo de bounding boxes para os dentes não encontrado ou não marcado como isTeethBoxes."
        );
      }
    },
    createCuspHeightMap(visible = true) {
      const existingHeightMapGroup =
        this.scene.getObjectByName("Cusps Height Map");
      if (existingHeightMapGroup) {
        this.scene.remove(existingHeightMapGroup);
        existingHeightMapGroup.children.forEach((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }

      const heightMapGroup = new THREE.Group();
      heightMapGroup.name = "Cusps Height Map";
      heightMapGroup.visible = visible;
      heightMapGroup.userData.name = "Cusps Height Map";

      const teeth = this.findTeethInScene();

      const existingCuspsPositions = []; // Array para armazenar as posições das cúspides

      const minDistance = 0.25; // Defina a distância mínima desejada entre cúspides

      teeth.forEach((tooth) => {
        const toothNumber = tooth.userData.toothNumber;
        if (
          this.dentalMap[toothNumber] !== undefined &&
          this.dentalMap[toothNumber].cusps
        ) {
          const cusps = this.dentalMap[toothNumber].cusps;

          if (!cusps || cusps.length === 0) return;

          // Encontre a altura mínima e máxima das cúspides
          let minHeight = Infinity;
          let maxHeight = -Infinity;

          cusps.forEach((cusp) => {
            minHeight = Math.min(minHeight, cusp.y);
            maxHeight = Math.max(maxHeight, cusp.y);
          });

          // Criar uma esfera para cada cúspide com base na altura, se estiver distante o suficiente das existentes
          cusps.forEach((cusp) => {
            const normalizedHeight =
              (cusp.y - minHeight) / (maxHeight - minHeight);

            if (
              (normalizedHeight < 0.25 && toothNumber < 31) ||
              (normalizedHeight > 0.75 && toothNumber >= 31)
            ) {
              // Verificar a distância para todas as cúspides existentes
              let isFarEnough = true;

              existingCuspsPositions.forEach((existingPosition) => {
                const distance = existingPosition.distanceTo(cusp);
                if (distance < minDistance) {
                  isFarEnough = false;
                }
              });

              if (isFarEnough) {
                // Mapeie a altura normalizada para uma cor (azul = baixo, vermelho = alto)
                let invertColorMap = 0;
                if (toothNumber >= 31) invertColorMap = 1;
                const color = new THREE.Color().setHSL(
                  Math.abs(invertColorMap - normalizedHeight) * 0.7,
                  1.0,
                  0.5
                );

                const sphereGeometry = new THREE.SphereGeometry(0.25, 4, 4);
                const sphereMaterial = new THREE.MeshBasicMaterial({
                  color: color,
                });
                const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
                sphere.position.copy(cusp);
                heightMapGroup.add(sphere);

                // Armazena a posição da cúspide adicionada
                existingCuspsPositions.push(sphere.position.clone());
              }
            }
          });
        }
      });

      this.scene.add(heightMapGroup);
      return heightMapGroup;
    },
    createHeatMapBasedOnCentersOfMass(visible = true) {
      // Remover qualquer mapa de calor existente
      const existingHeatMapGroup = this.scene.getObjectByName("Heat Map");
      if (existingHeatMapGroup) {
        this.scene.remove(existingHeatMapGroup);
        existingHeatMapGroup.children.forEach((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }

      const heatMapGroup = new THREE.Group();
      heatMapGroup.name = "Heat Map";
      heatMapGroup.visible = visible;
      heatMapGroup.userData.name = "Heat Map";

      const teeth = this.findTeethInScene();

      // Função para calcular a influência de um centro de massa em um vértice
      const calculateInfluence = (vertex, centerOfMass) => {
        const distance = vertex.distanceTo(centerOfMass);
        if (distance > 4) return 0;
        return 1 / (distance * distance); // Quanto menor a distância, maior a influência
      };

      teeth.forEach((tooth) => {
        const toothNumber = tooth.userData.toothNumber;
        const geometry = tooth.geometry.clone(); // Clonar a geometria original
        const positionAttribute = geometry.getAttribute("position");
        const vertexCount = positionAttribute.count;

        const colors = new Float32Array(vertexCount * 3);

        for (let i = 0; i < vertexCount; i++) {
          const vertex = new THREE.Vector3(
            positionAttribute.getX(i),
            positionAttribute.getY(i),
            positionAttribute.getZ(i)
          );

          let totalInfluence = 0;

          // Iterar sobre todos os dentes e acumular influência, exceto o próprio dente
          for (const key in this.dentalMap) {
            if (parseInt(key) !== toothNumber) {
              const centerOfMass = this.dentalMap[key].centerOfMass;
              const influence = calculateInfluence(vertex, centerOfMass);
              totalInfluence += influence;
            }
          }

          // Normalizar a influência para um valor entre 0 e 1 (ou qualquer faixa desejada)
          const normalizedInfluence = Math.min(totalInfluence, 1.0);

          // Definir a cor com base na influência normalizada (azul para baixa influência, vermelho para alta)
          const color = new THREE.Color().setHSL(0.0, normalizedInfluence, 0.5);

          colors[i * 3] = color.r;
          colors[i * 3 + 1] = color.g;
          colors[i * 3 + 2] = color.b;
        }

        geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

        // Criar o material usando vertexColors
        const material = new THREE.MeshBasicMaterial({
          vertexColors: true,
        });

        const coloredTooth = new THREE.Mesh(geometry, material);
        coloredTooth.position.copy(tooth.position);
        coloredTooth.rotation.copy(tooth.rotation);
        coloredTooth.scale.copy(tooth.scale);

        heatMapGroup.add(coloredTooth);
      });

      this.scene.add(heatMapGroup);
      return heatMapGroup;
    },
    createToothHeightMap(visible = true) {
      // Remover qualquer mapa de altura existente
      const existingHeightMapGroup =
        this.scene.getObjectByName("Cusps Height Map");
      if (existingHeightMapGroup) {
        this.scene.remove(existingHeightMapGroup);
        existingHeightMapGroup.children.forEach((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }

      const heightMapGroup = new THREE.Group();
      heightMapGroup.name = "Cusps Height Map";
      heightMapGroup.visible = visible;
      heightMapGroup.userData.name = "Cusps Height Map";

      const teeth = this.findTeethInScene();

      teeth.forEach((tooth) => {
        const geometry = tooth.geometry.clone(); // Clonar a geometria original
        const positionAttribute = geometry.getAttribute("position");
        const vertexCount = positionAttribute.count;

        let minHeight = Infinity;
        let maxHeight = -Infinity;

        // Encontrar altura mínima e máxima
        for (let i = 0; i < vertexCount; i++) {
          const y = positionAttribute.getY(i);
          if (y < minHeight) minHeight = y;
          if (y > maxHeight) maxHeight = y;
        }

        // Arrays para armazenar as posições e cores filtradas
        const redPositions = [];
        const redColors = [];

        // Criar o atributo de cor baseado na altura dos vértices
        for (let i = 0; i < vertexCount; i++) {
          const y = positionAttribute.getY(i);
          const normalizedHeight = (y - minHeight) / (maxHeight - minHeight);
          let invertColorMap = 0;
          if (tooth.userData.toothNumber >= 31) invertColorMap = 1;

          // Calcular a cor
          const color = new THREE.Color().setHSL(
            Math.abs(invertColorMap - normalizedHeight) * 0.5,
            1.0,
            0.5
          );

          // Checar se a cor está próxima ao vermelho
          if (color.r > 0.98) {
            // Adicionar a posição e a cor ao array filtrado
            redPositions.push(
              positionAttribute.getX(i),
              positionAttribute.getY(i),
              positionAttribute.getZ(i)
            );
            redColors.push(color.r, color.g, color.b);
          }
        }

        // Se houver vértices filtrados, criar a nova geometria e adicionar ao grupo
        if (redPositions.length > 0) {
          const redGeometry = new THREE.BufferGeometry();
          redGeometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(redPositions, 3)
          );
          redGeometry.setAttribute(
            "color",
            new THREE.Float32BufferAttribute(redColors, 3)
          );

          // Criar o material usando vertexColors
          const material = new THREE.MeshBasicMaterial({
            vertexColors: true,
          });

          const coloredTooth = new THREE.Mesh(redGeometry, material);
          coloredTooth.position.copy(tooth.position);
          coloredTooth.rotation.copy(tooth.rotation);
          coloredTooth.scale.copy(tooth.scale);

          heightMapGroup.add(coloredTooth);
        }
      });

      this.scene.add(heightMapGroup);
      return heightMapGroup;
    },
    createToothHeightMap_(visible = true) {
      // Remover qualquer mapa de altura existente
      const existingHeightMapGroup =
        this.scene.getObjectByName("Cusps Height Map");
      if (existingHeightMapGroup) {
        this.scene.remove(existingHeightMapGroup);
        existingHeightMapGroup.children.forEach((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }

      const heightMapGroup = new THREE.Group();
      heightMapGroup.name = "Cusps Height Map";
      heightMapGroup.visible = visible;
      heightMapGroup.userData.name = "Cusps Height Map";

      const teeth = this.findTeethInScene();

      teeth.forEach((tooth) => {
        const geometry = tooth.geometry.clone(); // Clonar a geometria original
        const positionAttribute = geometry.getAttribute("position");
        const vertexCount = positionAttribute.count;

        let minHeight = Infinity;
        let maxHeight = -Infinity;

        // Encontrar altura mínima e máxima
        for (let i = 0; i < vertexCount; i++) {
          const y = positionAttribute.getY(i);
          if (y < minHeight) minHeight = y;
          if (y > maxHeight) maxHeight = y;
        }

        // Criar o atributo de cor baseado na altura dos vértices
        const colors = new Float32Array(vertexCount * 3);
        for (let i = 0; i < vertexCount; i++) {
          const y = positionAttribute.getY(i);
          const normalizedHeight = (y - minHeight) / (maxHeight - minHeight);
          let invertColorMap = 0;
          if (tooth.userData.toothNumber >= 31) invertColorMap = 1;
          const color = new THREE.Color().setHSL(
            Math.abs(invertColorMap - normalizedHeight) * 0.5,
            1.0,
            0.5
          );

          colors[i * 3] = color.r;
          colors[i * 3 + 1] = color.g;
          colors[i * 3 + 2] = color.b;
        }

        geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

        // Criar o material usando vertexColors
        const material = new THREE.MeshBasicMaterial({
          vertexColors: true,
        });

        const coloredTooth = new THREE.Mesh(geometry, material);
        coloredTooth.position.copy(tooth.position);
        coloredTooth.rotation.copy(tooth.rotation);
        coloredTooth.scale.copy(tooth.scale);

        heightMapGroup.add(coloredTooth);
      });

      this.scene.add(heightMapGroup);
      return heightMapGroup;
    },

    identifyCusps(dentalMap) {
      const teeth = this.findTeethInScene();

      teeth.forEach((tooth) => {
        const toothNumber = tooth.userData.toothNumber;
        const toothData = dentalMap[toothNumber];
        const geometry = tooth.geometry.clone();
        geometry.applyMatrix4(tooth.matrixWorld);

        // Calcule as normais e determine a orientação
        geometry.computeVertexNormals();
        const normals = geometry.attributes.normal.array;
        let averageNormal = new THREE.Vector3(0, 0, 0);

        for (let i = 0; i < normals.length; i += 3) {
          averageNormal.add(
            new THREE.Vector3(normals[i], normals[i + 1], normals[i + 2])
          );
        }
        averageNormal.divideScalar(normals.length / 3).normalize();

        // Identificar os vértices mais altos (cúspides)
        const vertices = geometry.attributes.position.array;
        const cusps = [];

        for (let i = 0; i < vertices.length; i += 3) {
          const vertex = new THREE.Vector3(
            vertices[i],
            vertices[i + 1],
            vertices[i + 2]
          );
          // Use a normal média para decidir qual eixo é o "topo"
          const heightValue = vertex.dot(averageNormal);
          cusps.push({ position: vertex, height: heightValue });
        }

        // Ordenar cúspides pela altura (pico mais alto)
        cusps.sort((a, b) => b.height - a.height);

        if (toothData !== undefined) {
          // Salvar as cúspides mais altas no dentalMap
          toothData.cusps = cusps
            .slice(0, toothData.expectedCuspsCount)
            .map((c) => c.position);

          // Atualizar o dentalMap com as cúspides encontradas
          dentalMap[toothNumber] = toothData;
        }
      });

      return dentalMap;
    },
    setMeshCategory(mesh, category) {
      mesh.userData.category = category;
      return;
    },
    filterMeshes(category) {
      if (category === "") {
        this.showAllMeshes();
        return;
      }
      console.warn(`Filter Category ${category}`);
      this.scene.traverse((object) => {
        if (object.isMesh || object.isGroup) {
          // Verifica se o parâmetro é uma string ou um array de strings
          if (typeof category === "string") {
            // Filtra apenas pela categoria passada como string
            object.visible = object.userData.category === category;
          } else if (Array.isArray(category)) {
            // Filtra por múltiplas categorias
            object.visible = category.includes(object.userData.category);
          } else {
            // Caso o parâmetro não seja válido, esconde o mesh
            object.visible = false;
          }
        }
      });
    },
    showAllMeshes() {
      this.scene.traverse((object) => {
        if (object.isMesh) {
          object.visible = true;
        }
      });
    },
    createBoundingBoxMeshForTeeth(visible = true) {
      const existingBoundingBoxGroup =
        this.scene.getObjectByName("Teeth Boxes");
      if (existingBoundingBoxGroup) {
        this.scene.remove(existingBoundingBoxGroup);
        existingBoundingBoxGroup.children.forEach((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }

      const teeth = this.findTeethInScene();
      const boundingBoxGroup = new THREE.Group();
      const teethCenters = {}; // Objeto para armazenar os centros indexados pelo número do dente

      boundingBoxGroup.name = "Teeth Boxes";
      boundingBoxGroup.visible = visible;
      boundingBoxGroup.userData.name = "Teeth Boxes";
      boundingBoxGroup.userData.isTeethBoxes = true;

      teeth.forEach((tooth) => {
        const toothNumber = tooth.userData.toothNumber;
        const toothData = this.dentalMap[toothNumber];
        const boundingBox = new THREE.Box3().setFromObject(tooth);
        const helper = new THREE.Box3Helper(boundingBox, 0xa0a0a0);
        boundingBoxGroup.add(helper);

        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        const sphereGeometry = new THREE.SphereGeometry(0.75, 32, 32);
        const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(center);
        boundingBoxGroup.add(sphere);

        teethCenters[toothNumber] = center;

        if (toothData !== undefined) {
          // Adiciona o centro ao objeto teethCenters usando toothNumber como chave
          const medialVector = new THREE.Vector3(
            toothData.medial.x,
            toothData.medial.y,
            toothData.medial.z
          );
          const distalVector = new THREE.Vector3(
            toothData.distal.x,
            toothData.distal.y,
            toothData.distal.z
          );

          // Linha entre pontos medial e distal
          const lineGeometry = new THREE.BufferGeometry().setFromPoints([
            medialVector,
            distalVector,
          ]);
          const lineMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
          const line = new THREE.Line(lineGeometry, lineMaterial);
          boundingBoxGroup.add(line);

          // Linha pivot
          const midPoint = new THREE.Vector3()
            .addVectors(medialVector, distalVector)
            .multiplyScalar(0.5);
          const pivotGeometry = new THREE.BufferGeometry().setFromPoints([
            midPoint,
            center,
          ]);
          const pivotMaterial = new THREE.LineBasicMaterial({
            color: 0xff00ff,
          });
          const pivotLine = new THREE.Line(pivotGeometry, pivotMaterial);
          boundingBoxGroup.add(pivotLine);

          this.dentalMap[toothNumber].radius =
            tooth.geometry.boundingSphere.radius; // Atualiza o raio no dentalMap

          // Aqui continua a lógica de cálculo e atualização dos ângulos no dentalMap, conforme o original
          const transformedGeometry = this.applyTransformToGeometry(
            tooth.geometry,
            tooth.matrixWorld
          );
          const normals = transformedGeometry.attributes.normal.array;
          let averageNormal = new THREE.Vector3(0, 0, 0);

          for (let i = 0; i < normals.length; i += 3) {
            averageNormal.add(
              new THREE.Vector3(normals[i], normals[i + 1], normals[i + 2])
            );
          }
          averageNormal.divideScalar(normals.length / 3).normalize();

          // Aqui, você calcularia os ângulos com base na lógica específica do seu projeto
          // Exemplo genérico abaixo
          let angleXRad = Math.atan2(averageNormal.y, averageNormal.z);
          let angleYRad = Math.atan2(averageNormal.x, averageNormal.z);
          let angleZRad = Math.atan2(averageNormal.y, averageNormal.x);

          let angleXDeg = angleXRad * (180 / Math.PI);
          let angleYDeg = angleYRad * (180 / Math.PI);
          let angleZDeg = angleZRad * (180 / Math.PI);

          // Atualiza os ângulos no dentalMap
          this.dentalMap[toothNumber].angles = {
            x: angleXDeg,
            y: angleYDeg,
            z: angleZDeg,
          };
          // Supondo que actualAngles sejam calculados de maneira semelhante
          this.dentalMap[toothNumber].actualAngles = {
            x: angleXDeg,
            y: angleYDeg,
            z: angleZDeg,
          };
        }
      });

      // Armazena os centros no userData da cena para uso posterior
      this.scene.userData.teethCenters = teethCenters;

      this.scene.add(boundingBoxGroup);
      return boundingBoxGroup;
    },
    visualizeDentalEigenvectors(toothNumber, newCenter, parent) {
      const eigenvectors = this.dentalMap[toothNumber].eigenvectors;
      const centroid = this.dentalMap[toothNumber].centroid;
      const dimensions = this.dentalMap[toothNumber].dimensions; // Assumindo que dimensões estejam armazenadas

      if (!eigenvectors || !centroid || !dimensions) {
        console.warn(
          "Dados necessários para o dente",
          toothNumber,
          "não encontrados."
        );
        return;
      }

      // Criando e orientando a OBB baseada nos eigenvectors e dimensões
      const obb = this.createAndOrientOBB(eigenvectors, dimensions, centroid);
      // Transladar a OBB para o newCenter, se necessário
      obb.position.copy(newCenter);

      // Adiciona a OBB ao grupo especificado
      parent.add(obb);

      // Converte o centroide para um objeto THREE.Vector3 para fácil manipulação
      const centroidVec = new THREE.Vector3(centroid.x, centroid.y, centroid.z);

      eigenvectors.data.forEach((vec, index) => {
        const direction = new THREE.Vector3(
          vec["0"],
          vec["1"],
          vec["2"]
        ).normalize();
        const length = 8; // Comprimento dos ArrowHelpers
        const color = [0xff0000, 0x00ff00, 0x0000ff][index % 3]; // Cores variadas para os ArrowHelpers

        // Cria um ArrowHelper no centroide do objeto
        const arrowHelper = new THREE.ArrowHelper(
          direction,
          centroidVec,
          length,
          color
        );

        // Calcula a diferença entre o centro atual da bounding box e o centroide
        const centerDiff = new THREE.Vector3(
          newCenter.x,
          newCenter.y,
          newCenter.z
        ).sub(centroidVec);

        // Translada o ArrowHelper para o novo centro
        arrowHelper.position.add(centerDiff);

        // Adiciona o ArrowHelper ao grupo especificado
        parent.add(arrowHelper);
      });
    },
    createAndOrientOBB(eigenvectors, dimensions, centroid) {
      let obbGeometry = new THREE.BoxGeometry(
        dimensions[0],
        dimensions[1],
        dimensions[2]
      );
      let obbMesh = new THREE.Mesh(
        obbGeometry,
        new THREE.MeshBasicMaterial({ color: 0xff0000, wireframe: true })
      );

      // Suponha que eigenvectors.data[0] é o eixo X, eigenvectors.data[1] é o eixo Y, e eigenvectors.data[2] é o eixo Z.
      let xAxis = new THREE.Vector3()
        .fromArray(eigenvectors.data[0])
        .normalize();
      let yAxis = new THREE.Vector3()
        .fromArray(eigenvectors.data[1])
        .normalize();
      let zAxis = new THREE.Vector3()
        .fromArray(eigenvectors.data[2])
        .normalize();

      // Preparar a matriz de rotação dos eigenvectors
      let rotationMatrix = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);

      obbMesh.setRotationFromMatrix(rotationMatrix);
      obbMesh.position.set(centroid.x, centroid.y, centroid.z); // Posiciona inicialmente no centroide

      return obbMesh;
    },
    toggleMethodsExecution() {
      if (this.intervalId) {
        // Se intervalId não é nulo, interrompe a execução
        clearInterval(this.intervalId);
        this.intervalId = null;
      } else {
        // Inicia a execução
        this.intervalId = setInterval(() => {
          //this.createBoundingBoxMeshForTeeth(true);
          //this.drawDentalArch();
          this.moveDentalToIdealPosition();
          //this.generateGumCurve(this.dentalMap)
        }, 500); // 500 milissegundos = meio segundo
      }
    },
    initVoxelGrid() {
      const teeth = this.findTeethInScene(); // Assume que findTeethInScene já está definido no seu sistema
      this.voxelGrid = new VoxelGrid(teeth, this.voxelSize);
    },
    queryVoxelGrid(position) {
      if (this.voxelGrid) {
        const mixedVoxels = this.voxelGrid.query(position);
        console.log(mixedVoxels);
        // Faça algo com os voxels mistos encontrados
      }
    },
    findSecondLowestPoints(mesh, radius = 2, baito, n) {
      const vertices = mesh.geometry.attributes.position.array;
      const points = [];

      // Extrair os vértices e ordená-los por Y (altura)
      for (let i = 0; i < vertices.length; i += 3) {
        points.push(
          new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2])
        );
      }
      points.sort((a, b) => a.y - b.y);

      if (baito.length >= n) {
        return baito;
      }

      let secondBestPoint = null;
      let maxNeighbors = 0;

      // Iterar pelos pontos ordenados
      for (const point of points) {
        let validPoint = true;

        // Verificar se o ponto está contido em baito
        for (const baitoPoint of baito) {
          if (point.equals(baitoPoint)) {
            validPoint = false;
            break;
          }
        }

        if (!validPoint) continue;

        let neighborCount = 0;

        // Contar vizinhos dentro do raio especificado
        for (const otherPoint of points) {
          if (
            !baito.includes(otherPoint) &&
            point.distanceTo(otherPoint) < radius
          ) {
            neighborCount++;
          }
        }

        // Verificar se é melhor que o ponto atualmente selecionado
        if (neighborCount > maxNeighbors) {
          // Verificar se o ponto está suficientemente distante dos pontos em baito
          let farEnough = true;
          for (const baitoPoint of baito) {
            if (point.distanceTo(baitoPoint) < radius * 2) {
              farEnough = false;
              break;
            }
          }

          if (farEnough) {
            secondBestPoint = point;
            maxNeighbors = neighborCount;
          }
        }
      }

      if (secondBestPoint) {
        baito.push(secondBestPoint); // Adicionar o ponto encontrado a baito para evitar repetições
      }

      // Chamada recursiva para encontrar mais pontos
      return this.findSecondLowestPoints(mesh, radius, baito, n);
    },
    findLowestPoints(mesh, radius = 2) {
      const vertices = mesh.geometry.attributes.position.array;
      const points = [];

      // Extrair os vértices e ordená-los por Y (altura)
      for (let i = 0; i < vertices.length; i += 3) {
        points.push(
          new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2])
        );
      }
      points.sort((a, b) => a.y - b.y);

      let bestPoints = [];
      let maxNeighbors = 0;
      let maxHeightDifference = 1;

      // Iterar pelos pontos ordenados
      for (let i = 0; i < points.length; i++) {
        const point = points[i];

        // Verificar se está dentro da diferença máxima de altura
        if (point.y - points[0].y > maxHeightDifference) {
          break; // Se ultrapassou a diferença máxima de altura, parar a busca
        }

        let neighborCount = 0;

        // Contar vizinhos dentro do raio especificado
        for (let j = 0; j < points.length; j++) {
          if (i !== j && point.distanceTo(points[j]) < radius) {
            neighborCount++;
          }
        }

        // Verificar se é melhor que os pontos atuais selecionados
        if (neighborCount > maxNeighbors) {
          // Verificar se o ponto está suficientemente distante dos pontos selecionados
          let tooClose = false;
          for (const selectedPoint of bestPoints) {
            if (point.distanceTo(selectedPoint) < radius * 2) {
              tooClose = true;
              break;
            }
          }

          if (!tooClose) {
            bestPoints = [point];
            maxNeighbors = neighborCount;
          }
        } else if (neighborCount === maxNeighbors) {
          // Se tiver o mesmo número de vizinhos, adicionar à lista
          bestPoints.push(point);
        }
      }

      const secondBest = this.findSecondLowestPoints(
        mesh,
        radius,
        [bestPoints[0]],
        10
      );
      return secondBest;
    },

    createCentersMesh(points, radius) {
      const centersMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
      const areaMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.05,
      });

      points.forEach((point) => {
        // Criar a esfera azul no ponto encontrado
        const sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);
        const sphere = new THREE.Mesh(sphereGeometry, centersMaterial);
        sphere.position.copy(point);
        this.scene.add(sphere);

        // Criar a esfera transparente branca para mostrar a área de atuação
        const areaGeometry = new THREE.SphereGeometry(radius, 32, 32);
        const areaSphere = new THREE.Mesh(areaGeometry, areaMaterial);
        areaSphere.position.copy(point);
        this.scene.add(areaSphere);
      });
    },
    handleKeyUp(event) {
      this.isAltPressed = event.altKey;
      this.isShiftPressed = event.shiftKey;
      if (this.currentlySelectedTooth) {
        this.updateToothShadows();
      }
    },

    visualizeClusters(dbscanResult, radius) {
      // Limpar objetos antigos (centroides e ruído)
      this.scene.children.forEach((obj) => {
        if (obj.userData.isClusterSphere || obj.userData.isNoise) {
          this.scene.remove(obj);
        }
      });

      if (!dbscanResult || !Array.isArray(dbscanResult.clusters)) return;

      // Paleta de cores (adicione mais se quiser)
      const colors = [
        0x800080, // Roxo
      ];

      dbscanResult.clusters.forEach((cluster, clusterIndex) => {
        const color = colors[clusterIndex % colors.length];

        // Renderizar esfera do centroide
        if (cluster.centroide) {
          const geometry = new THREE.SphereGeometry(radius, 4, 4);
          const material = new THREE.MeshPhongMaterial({
            color: color,
            transparent: true,
            opacity: 0.25,
            specular: 0x111111,
            shininess: 30,
          });

          const sphere = new THREE.Mesh(geometry, material);
          sphere.position.set(
            cluster.centroide.x,
            cluster.centroide.y,
            cluster.centroide.z
          );
          sphere.userData.isClusterSphere = true;
          this.scene.add(sphere);
        }
      });
    },
    handleKeyDown(event) {
      const step = 0.1; // Quantidade a mover a esfera a cada pressionamento de tecla

      this.isAltPressed = event.altKey;
      this.isShiftPressed = event.shiftKey;
      if (this.currentlySelectedTooth) {
        this.updateToothShadows();
      }

      if (this.sphereMesh)
        switch (event.key) {
          case "ArrowUp":
            this.moveSphere(
              new THREE.Vector3(0, 1, 0)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "ArrowDown":
            this.moveSphere(
              new THREE.Vector3(0, -1, 0)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "ArrowLeft":
            this.moveSphere(
              new THREE.Vector3(-1, 0, 0)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "ArrowRight":
            this.moveSphere(
              new THREE.Vector3(1, 0, 0)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "PageUp":
            this.moveSphere(
              new THREE.Vector3(0, 0, 1)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "PageDown":
            this.moveSphere(
              new THREE.Vector3(0, 0, -1)
                .multiplyScalar(step)
                .add(this.sphere.position)
            );
            break;
          case "Home":
            console.table(this.sphere.position);
            console.table(this.findCylindricalSurfaces(this.activeObject));
            break;
          default:
            break;
        }
      if (this.transformControl) {
        if (event.key === "=") {
          this.saveToothClickPointsJSON();
        }
        if (event.key === "y") {
          console.warn("Obtendo dados das bordas ...");
          const spheresMap = this.analyzeSpheresBVH(
            this.activeObject.geometry,
            0.25
          );
          console.warn("Criando cenário...");
          createScenario(this.activeObject, spheresMap); // Criar o cenário

          // // Preparar os dados
          // console.warn('Preparando dados para tensorFlow...')
          // const { inputs, labels } = prepareData(scenario);
          // // Acumule os dados usando tf.concat
          // this.globalDataStore.inputs = this.globalDataStore.inputs.concat(inputs)
          // this.globalDataStore.labels = this.globalDataStore.labels.concat(labels)
          // console.warn('Dados para tensorFlow acumulados.')
        }
        if (event.key === "u") {
          this.dbLoadTrainingCounter();
          this.showTrainingDialog = true;
        } else if (event.key === "t") {
          this.statusMessage = "Aplicando IA ao modelo...";
          setTimeout(() => {
            this.applyModelToMesh();
          }, 500);
        }
        if (event.key === "r") {
          this.generateRotatedImages();
        } else if (event.key === "a") {
          this.addAxisPlanes();
        } else if (event.key === "c") {
          //this.commitTransform();
          this.statusMessage = "Obtendo dados clusterizados...";
          extractToothCrownVertices(this.activeObject, this.dentalMap);
          this.statusMessage = "Clusters obtidos";
        } else if (event.key === "+") {
          if (this.selectedTooth) {
            this.dentalMap[this.selectedTooth].dijkstraConfig.saltoMax += 0.1;
            this.updateInterfaceWithDentalData(this.dentalMap[this.selectedTooth])
            console.warn(`SaltoMax ${this.dentalMap[this.selectedTooth].dijkstraConfig.saltoMax}`)
          }
        }else if (event.key === "-") {
          if (this.selectedTooth) {
            this.dentalMap[this.selectedTooth].dijkstraConfig.saltoMax -= 0.1;
            this.updateInterfaceWithDentalData(this.dentalMap[this.selectedTooth])
            console.warn(`SaltoMax ${this.dentalMap[this.selectedTooth].dijkstraConfig.saltoMax}`)
          }
        }else if (event.key === "b") {
          this.createBoundingBoxMeshForTeeth();
          this.drawDentalArch();
        } else if (event.key === "m") {
          //this.toggleMethodsExecution();
          this.cleanGumFragments();
          this.alignDentalMapToArch();
          //this.alignInterdental();
          this.alignDentalAngularToTangent();
          this.closeGapsBetweenTeethAndGum();
        } else if (event.key === "p") {
          const partialGeometry = this.activeObject.geometry;
          const seeds = this.findSeedPoints(partialGeometry, "maxillary");
          let segmentedPoints = new Set();
          seeds.forEach((seedPoint) => {
            this.regionGrowingRecursive(
              seedPoint,
              partialGeometry,
              4,
              segmentedPoints
            );
          });
          this.scene.add(
            this.visualizeConqueredVertices(partialGeometry, segmentedPoints)
          );
        } else if (event.key === ".") {
          this.adjustArchScale(this.activeArchMoveType, 1.002);
          this.alignDentalMapToArch();
          this.alignDentalAngularToTangent();
          this.closeGapsBetweenTeethAndGum();
        } else if (event.key === ",") {
          this.adjustArchScale(this.activeArchMoveType, 0.998);
          this.alignDentalMapToArch();
          this.alignDentalAngularToTangent();
          this.closeGapsBetweenTeethAndGum();
        } else if (event.key === "n") {
          //this.scanDentalMapAndStartWorker()
          this.scanDentalMapforMiniGun();
        } else if (event.key === "%") {
          this.simpleAnalysis();
        } else if (event.key === "h") {
          //this.initializeOctreeWithTeeth();
          //this.generateVertexColorMesh();
          this.initVoxelGrid();
          console.table(this.voxelGrid);
          const closestVertices = this.voxelGrid.findClosestVertices();
          const numberOfObjects = Object.keys(closestVertices).length;
          console.log(numberOfObjects);
          this.voxelGrid.generateVertexColorMesh(this);
        } else if (event.key === "s") {
          this.drawSphere();
        } else if (event.key === "o") {
          const teethUpper = this.findTeethInScene("upper");
          const projectedImageUpper = this.generate2DProjection(
            teethUpper,
            true
          );
          this.drawImageInDiv(projectedImageUpper, "vision2D");

          const teethLower = this.findTeethInScene("lower");
          const projectedImageLower = this.generate2DProjection(teethLower);
          this.drawImageInDiv(projectedImageLower, "vision2D", false);
        } else if (event.key === "Home") {
          this.filterMeshes("base");
        } else if (event.key === "End") {
          this.showAllMeshes();
        } else if (event.key === "PageUp") {
          this.filterMeshes(["segmented", "tooth", "gums"]);
        }
      }
      if (event.key === "6") {
        //this.currentIndex.col = (this.currentIndex.col + 1) % this.dataMatrix[0].length;
        this.miniGun.neighbor += 5;
        this.updateMarker();
      } else if (event.key === "4") {
        //this.currentIndex.col = (this.currentIndex.col - 1 + this.dataMatrix[0].length) % this.dataMatrix[0].length;
        this.miniGun.neighbor -= 5;
        this.updateMarker();
      } else if (event.key === "8") {
        //this.currentIndex.row = (this.currentIndex.row - 1 + this.dataMatrix.length) % this.dataMatrix.length;
        this.miniGun.variation += 5;
        this.updateMarker();
      } else if (event.key === "2") {
        //this.currentIndex.row = (this.currentIndex.row + 1) % this.dataMatrix.length;
        this.miniGun.variation -= 5;
        this.updateMarker();
      } else if (event.key === "9") {
        //this.currentIndex.row = (this.currentIndex.row - 1 + this.dataMatrix.length) % this.dataMatrix.length;
        this.miniGun.angleBetween += 1;
        this.updateMarker();
      } else if (event.key === "7") {
        //this.currentIndex.row = (this.currentIndex.row + 1) % this.dataMatrix.length;
        this.miniGun.angleBetween -= 1;
        this.updateMarker();
      } else if (event.key === "*") {
        //this.currentIndex.row = (this.currentIndex.row + 1) % this.dataMatrix.length;
        this.miniGun.active = !this.miniGun.active;
        this.iaActive = this.miniGun.active ? "yellow" : "gray";
        this.statusMessage = this.miniGun.active ? "Learning On" : "";
        //this.gumSearch();
      } else if (event.key === "/") {
        // Procurar o objeto gengiva e inicializar a física
        const gum = this.findGumInScene();
        this.physicsMode = !this.physicsMode;

        if (gum && this.physicsMode) {
          console.log("Gengiva encontrada:", gum);
          this.initSimplePhysics(gum); // Inicializar a física na gengiva
          const teeth = this.findTeethInScene();
          if (teeth.length) {
            this.applyPhysics(teeth, gum); // Configurar interação entre dentes e gengiva
          }
        } else {
          console.error("Gengiva não encontrada na cena.");
        }
      } else if (event.key === "g") {
        this.makeGradientMesh();
      }
      // Se estiver no modo de marcação de contorno e a tecla Delete for pressionada
      if (
        (this.$store.state.editor3D.currentAction === "markingContour" ||
          this.$store.state.editor3D.currentAction === "creatingDentalArch") &&
        event.key === "Delete"
      ) {
        // Chame um método para remover o último ponto do contorno
        this.removeLastContourPoint();
      } else if (
        event.key === "Delete" &&
        this.currentAction === "definingMedialDistalPoints" &&
        this.selectedTooth
      ) {
        this.dentalMap[this.selectedTooth].medial = null;
        this.dentalMap[this.selectedTooth].distal = null;
        this.updateInterfaceWithDentalData(this.dentalMap[this.selectedTooth]);
      } else if (this.activeObject && event.key === "Delete") {
        // Se um objeto estiver ativo e a tecla Delete for pressionada
        this.removeObjectFromScene(this.activeObject);
        this.activeObject = null;
      }
    },
    removeLastContourPoint() {
      // Despache uma ação para remover o último ponto do contorno
      this.$store.dispatch("editor3D/removeLastContourPoint");
      // Atualize o contorno na cena
      this.updateContour();
    },
    toggleJaw(arcType) {
      this.scene.children.forEach((objeto) => {
        if (objeto.userData.modelType !== undefined) {
          if (objeto.userData.modelType === arcType) {
            objeto.visible = !objeto.visible;
          }
        }
      });
    },
    toggleBoxHelper() {
      this.scene.children.forEach((objeto) => {
        if (objeto.userData.boxHelper !== undefined) {
          objeto.visible = !objeto.visible;
        }
      });
    },
    toggleLight(type) {
      this.scene.children.forEach((objeto) => {
        if (objeto.userData.light !== undefined) {
          if (objeto.userData.light === type) {
            objeto.visible = !objeto.visible;
          }
        }
      });
    },
    removeTriangle(activeMesh, triangleIndex) {
      if (!activeMesh || !activeMesh.geometry) {
        console.error("Nenhum objeto ativo ou geometria definida.");
        return;
      }

      const geometry = activeMesh.geometry;
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal.array;

      // Calculando os índices corretos para remoção
      const start = triangleIndex * 9; // Cada triângulo tem 9 valores (3 vértices * 3 coordenadas)
      const end = start + 9;

      // Criando novos arrays excluindo os dados do triângulo selecionado
      const newPositionArray = [
        ...positions.slice(0, start),
        ...positions.slice(end, positions.length),
      ];
      const newNormalArray = [
        ...normals.slice(0, start),
        ...normals.slice(end, normals.length),
      ];

      // Atualizando a geometria
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(newPositionArray), 3)
      );
      geometry.setAttribute(
        "normal",
        new THREE.BufferAttribute(new Float32Array(newNormalArray), 3)
      );

      // Se a geometria tiver índices, você precisará atualizá-los aqui

      // Sinalizando a necessidade de atualização
      geometry.attributes.position.needsUpdate = true;
      geometry.attributes.normal.needsUpdate = true;
    },
    handleModelClick(event) {
      const intersection = this.getPointOnModel(event);
      if (!intersection) return;

      if (this.currentMode !== "") return;

      const currentAction = this.$store.state.editor3D.currentAction;
      console.table(intersection);

      //console.table(this.analyzeClick(intersection,this.activeObject.geometry))

      if (this.miniGun.active) {
        this.miniGun.object = this.activeObject.clone();
        this.miniGun.point = intersection.point;
        this.updateMarker();
      }

      if (currentAction === "creatingOcclusionPlan") {
        this.addPlaneSphereAtPoint(intersection.point);
        this.$store.dispatch("editor3D/addPointToOcclusionPlan", intersection);
      } else if (currentAction === "markingContour") {
        this.$store.dispatch("editor3D/addContourPoint", intersection.point);
        this.updateContour(); // Atualiza a visualização do contorno
      } else if (currentAction === "creatingDentalArch") {
        //this.addPlaneSphereAtPoint(intersection.point);
        this.$store.dispatch(
          "editor3D/addPointToDentalArch",
          intersection.point
        );
        this.updateContour(); // Atualiza a visualização do contorno
      } else if (
        intersection.objectRef.userData.objectType &&
        intersection.objectRef.userData.objectType === "tooth"
      ) {
        this.transformControl.attach(intersection.objectRef);
        this.analyzeAdjacentTeeth(intersection.objectRef.userData.toothNumber);
      } else if (currentAction === "definingMedialDistalPoints") {
        if (this.selectedTooth) {
          if (!this.dentalMap[this.selectedTooth].medial) {
            this.dentalMap[this.selectedTooth].medial =
              intersection.point.clone();
            this.updateInterfaceWithDentalData(
              this.dentalMap[this.selectedTooth]
            );
          } else if (!this.dentalMap[this.selectedTooth].distal) {
            this.dentalMap[this.selectedTooth].distal =
              intersection.point.clone();
            console.table({
              tooth: this.selectedTooth,
              dentalMapa: this.dentalMap[this.selectedTooth],
            });
            this.$store.dispatch("editor3D/enterMedialDistalEditMode", 0);
            this.updateInterfaceWithDentalData(
              this.dentalMap[this.selectedTooth]
            );
          }
        }
      } else if (currentAction === "definingArchorPoints") {
        if (this.selectedTooth) {
          const toothData = this.dentalMap[this.selectedTooth];

          if (!toothData.archorPoints) {
            toothData.archorPoints = [];
          }

          const clickedPoint = intersection.point.clone();

          // Valida se há dados no dbScan
          if (!toothData.dbScan || !Array.isArray(toothData.dbScan.clusters)) {
            console.warn(
              "dbScan ou clusters ausentes para o dente selecionado."
            );
            return;
          }

          // Busca pelo centroide mais próximo ao clique
          let closestCentroid = null;
          let minDistance = Infinity;

          toothData.dbScan.clusters.forEach((cluster) => {
            const c = cluster.centroide;
            const centroidVec = new THREE.Vector3(c.x, c.y, c.z);
            const dist = centroidVec.distanceTo(clickedPoint);
            if (dist < minDistance) {
              minDistance = dist;
              closestCentroid = centroidVec;
            }
          });

          if (!closestCentroid) return;

          const indexToRemove = toothData.archorPoints.findIndex((pt) => {
            const point = pt.isVector3
              ? pt
              : new THREE.Vector3(pt.x, pt.y, pt.z);
            return point.distanceTo(closestCentroid) < 1;
          });

          if (indexToRemove !== -1) {
            // Remove o ponto se já existir próximo
            toothData.archorPoints.splice(indexToRemove, 1);
          } else if (toothData.archorPoints.length < 4) {
            // Adiciona o centroide mais próximo se ainda houver espaço
            toothData.archorPoints.push(closestCentroid);
          }

          // Atualiza a visualização
          this.updateInterfaceWithDentalData(toothData);
        }
      }
    },
    scanDentalMapAndStartWorker() {
      if (!this.activeObject) {
        console.warn("No active object selected.");
        return;
      }

      Object.keys(this.dentalMap).forEach((selectedTooth) => {
        const toothData = this.dentalMap[selectedTooth];
        if (toothData.medial && toothData.distal) {
          this.startSegmentationWorker(selectedTooth);
        }
      });
    },
    startSegmentationWorker(selectedTooth) {
      // Obtenha os dados de posição da malha ativa (supondo que activeObject é uma malha)
      const positions = this.activeObject.geometry.attributes.position.array;
      const normals = this.activeObject.geometry.attributes.normal.array;

      // Suponha que medialPoint e distalPoint já estão definidos
      const medialPoint = this.dentalMap[selectedTooth].medial;
      const distalPoint = this.dentalMap[selectedTooth].distal;

      // Calcula o ponto central e o raio para a busca
      const centerPoint = {
        x: (medialPoint.x + distalPoint.x) / 2,
        y: (medialPoint.y + distalPoint.y) / 2,
        z: (medialPoint.z + distalPoint.z) / 2,
      };
      const length = Math.sqrt(
        (distalPoint.x - medialPoint.x) ** 2 +
          (distalPoint.y - medialPoint.y) ** 2 +
          (distalPoint.z - medialPoint.z) ** 2
      );
      const searchRadius = length;

      // Os dados precisam ser copiados para um novo ArrayBuffer se eles não forem instâncias de ArrayBuffer
      const positionsArrayBuffer = positions.buffer.slice(0);
      const normalsArrayBuffer = normals.buffer.slice(0);

      // Inicialize o Web Worker
      const worker = new Worker(
        new URL("@/workers/edgeFinder360.js", import.meta.url),
        { type: "module" }
      );

      this.iaActive = "orange";

      // Envie os dados clonados para o worker
      worker.postMessage(
        {
          positions: positionsArrayBuffer,
          normals: normalsArrayBuffer,
          toothId: selectedTooth,
          medialPoint: { x: medialPoint.x, y: medialPoint.y, z: medialPoint.z },
          distalPoint: { x: distalPoint.x, y: distalPoint.y, z: distalPoint.z },
          centerPoint: centerPoint,
          searchRadius: searchRadius,
        },
        [positionsArrayBuffer, normalsArrayBuffer]
      ); // Transfer list

      // Ouvir por mensagens do worker
      worker.onmessage = (event) => {
        // Atualize a interface com os pontos de borda recebidos
        const { type, data } = event.data;
        if (type == "result") {
          this.iaActive = this.iaActive === "orange" ? "gray" : "orange";
          const { borderPoints, toothId, dataMatrix } = data;
          this.dentalMap[toothId].borderPoints = borderPoints;
          this.updateInterfaceWithDentalData(this.dentalMap[toothId]);
          this.dataMatrix = dataMatrix;
          this.statusMessage = "";
          this.iaActive = "gray";
          // Encerra o worker
          worker.terminate();
        } else if (type == "point") {
          const geometry = new THREE.SphereGeometry(data.radius, 4, 4); // Tamanho da esfera ajustável
          const material = new THREE.MeshBasicMaterial({ color: data.color });
          const sphere = new THREE.Mesh(geometry, material);
          sphere.position.set(data.point.x, data.point.y, data.point.z);
          this.activeObject.add(sphere);
        } else if (type === "line") {
          // Cria a geometria da linha com dois pontos: start e end
          const material = new THREE.LineBasicMaterial({ color: data.color });
          const geometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(data.start.x, data.start.y, data.start.z),
            new THREE.Vector3(data.end.x, data.end.y, data.end.z),
          ]);
          const line = new THREE.Line(geometry, material);

          this.activeObject.add(line);
        } else if (type == "message") {
          const { text } = data;
          this.statusMessage = text;
          //console.warn(text);
        } else if (type == "sphere") {
          const geometry = new THREE.SphereGeometry(data.radius, 16, 16); // Tamanho da esfera ajustável
          const material = new THREE.MeshBasicMaterial({
            color: data.color,
            transparent: true,
            opacity: 0.25,
          });
          const sphere = new THREE.Mesh(geometry, material);

          sphere.position.set(data.point.x, data.point.y, data.point.z);
          this.activeObject.add(sphere);
        } else if (type === "bboxData") {
          const min = new THREE.Vector3(data.min.x, data.min.y, data.min.z);
          const max = new THREE.Vector3(data.max.x, data.max.y, data.max.z);

          // Cria os vértices da BoundingBox
          const vertices = [
            new THREE.Vector3(min.x, min.y, min.z),
            new THREE.Vector3(max.x, min.y, min.z),
            new THREE.Vector3(min.x, max.y, min.z),
            new THREE.Vector3(max.x, max.y, min.z),
            new THREE.Vector3(min.x, min.y, max.z),
            new THREE.Vector3(max.x, min.y, max.z),
            new THREE.Vector3(min.x, max.y, max.z),
            new THREE.Vector3(max.x, max.y, max.z),
          ];

          // Define as arestas da BoundingBox
          const edges = [
            [0, 1],
            [1, 3],
            [3, 2],
            [2, 0], // base inferior
            [4, 5],
            [5, 7],
            [7, 6],
            [6, 4], // base superior
            [0, 4],
            [1, 5],
            [2, 6],
            [3, 7], // lados verticais
          ];

          const material = new THREE.LineBasicMaterial({ color: 0xff00ff }); // Cor magenta para a BoundingBox

          edges.forEach((pair) => {
            const geometry = new THREE.BufferGeometry().setFromPoints([
              vertices[pair[0]],
              vertices[pair[1]],
            ]);
            const line = new THREE.Line(geometry, material);
            this.activeObject.add(line); // Adiciona a linha ao objeto ativo na cena
          });
        }
      };
    },
    addTriangleToGeometry(geometry, v1, v2, v3) {
      // Assume que v1, v2, e v3 são instâncias de THREE.Vector3 para os vértices do novo triângulo

      // Obtenha o atributo de posição existente
      const positionAttribute = geometry.getAttribute("position");
      const vertexCount = positionAttribute.count;

      // Crie um novo array com espaço extra para os novos vértices
      const newPositions = new Float32Array((vertexCount + 3) * 3);

      // Copie os vértices existentes para o novo array
      newPositions.set(positionAttribute.array);

      // Adicione os novos vértices no final do array
      const startIndex = vertexCount * 3;
      newPositions.set([v1.x, v1.y, v1.z], startIndex);
      newPositions.set([v2.x, v2.y, v2.z], startIndex + 3);
      newPositions.set([v3.x, v3.y, v3.z], startIndex + 6);

      // Atualize o atributo de posição com o novo array
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(newPositions, 3)
      );
      geometry.computeVertexNormals(); // Recalcular as normais para a iluminação correta

      // Informa ao Three.js que os atributos da geometria mudaram
      geometry.attributes.position.needsUpdate = true;
    },
    addTriangleToSmoothMesh(v1, v2, v3) {
      // Adiciona os vértices do triângulo à lista de vértices da geometria suavizada
      this.smoothGeometryVertices.push(v1.x, v1.y, v1.z);
      this.smoothGeometryVertices.push(v2.x, v2.y, v2.z);
      this.smoothGeometryVertices.push(v3.x, v3.y, v3.z);
    },
    updateActiveObject() {
      // Extrair os vértices existentes do activeObject
      const existingVertices =
        this.activeObject.geometry.getAttribute("position").array;

      // Criar um novo array para combinar os vértices existentes com os novos
      const combinedVertices = new Float32Array(
        existingVertices.length + this.smoothGeometryVertices.length
      );

      // Copiar os vértices existentes para o novo array
      combinedVertices.set(existingVertices);

      // Adicionar os novos vértices ao final do novo array
      combinedVertices.set(
        this.smoothGeometryVertices,
        existingVertices.length
      );

      // Criar a nova BufferGeometry com os vértices combinados
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(combinedVertices, 3)
      );

      // Calcula as normais para a iluminação
      geometry.computeVertexNormals();
      this.calculateVertexNormals(geometry);
      geometry.getAttribute("position").setUsage(THREE.DynamicDrawUsage);

      // Substitui a geometria do activeObject pela nova geometria
      this.activeObject.geometry.dispose(); // Descarta a geometria antiga
      this.activeObject.geometry = geometry;

      this.activeObject.geometry.attributes.position.needsUpdate = true;
      this.activeObject.geometry.attributes.normal.needsUpdate = true;
      this.activeObject.material.needsUpdate = true;

      this.activeObject.geometry.computeBoundingSphere();
      this.activeObject.geometry.computeBoundingBox();

      // Reseta os arrays para uso futuro
      this.newVertices = [];
    },
    createSmoothGeometry() {
      const vertices = new Float32Array(this.smoothGeometryVertices);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));

      let smoothMesh = this.scene.children.find(
        (obj) => obj.userData.name === "smoothMesh"
      );
      if (!smoothMesh) {
        // Se não existir, crie-o e adicione à cena
        const material = this.activeObject.material
          ? this.activeObject.material.clone()
          : new THREE.MeshBasicMaterial({
              color: 0x00ff00,
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 0.5,
            });
        smoothMesh = new THREE.Mesh(geometry, material);
        smoothMesh.userData.name = "smoothMesh";
        this.scene.add(smoothMesh);
      } else {
        // Se o smoothMesh já existe, atualize sua geometria
        smoothMesh.geometry.dispose(); // Descarte a geometria antiga
        smoothMesh.geometry = geometry;
        smoothMesh.geometry.attributes.position.needsUpdate = true; // Marca a geometria para atualização
      }
      smoothMesh.geometry.computeVertexNormals();
    },
    findClosestVertex(index, vertex, vertices) {
      let closestVertex = null;
      let shortestDistance = Infinity;

      vertices.forEach((v, i) => {
        // Ignora o próprio vértice baseado no índice
        if (
          i > index &&
          v.x !== vertex.x &&
          v.y !== vertex.y &&
          v.z !== vertex.z
        ) {
          const distance = vertex.distanceTo(v);
          if (distance < shortestDistance && distance <= 5) {
            closestVertex = i;
            shortestDistance = distance;
          }
        }
      });

      return closestVertex;
    },
    uniqueVerticesFunction(vertices) {
      const uniqueStrings = new Set();
      const uniqueVertices = [];

      vertices.forEach((v) => {
        const vString = JSON.stringify(v);
        if (!uniqueStrings.has(vString)) {
          uniqueStrings.add(vString);
          uniqueVertices.push(v.clone());
        }
      });

      return uniqueVertices;
    },
    deindexGeometry(indexedGeometry) {
      const nonIndexedGeometry = new THREE.BufferGeometry();

      // Se a geometria original é indexada, ela terá um atributo 'index'
      if (indexedGeometry.index) {
        const indices = indexedGeometry.index.array;
        const positions = indexedGeometry.attributes.position;
        const newPositions = [];
        // Se você tem outros atributos como normal, uv, etc, você deverá processá-los de maneira similar

        // Replicar vértices com base nos índices
        for (let i = 0; i < indices.length; i++) {
          const index = indices[i];
          newPositions.push(
            positions.getX(index),
            positions.getY(index),
            positions.getZ(index)
          );
        }

        // Atualizar a geometria não indexada com os novos dados
        nonIndexedGeometry.setAttribute(
          "position",
          new THREE.Float32BufferAttribute(newPositions, 3)
        );
        // Repetir para outros atributos se necessário
      } else {
        // Se a geometria não é indexada, simplesmente clonar
        return indexedGeometry.clone();
      }

      return nonIndexedGeometry;
    },
    identifyBorderVerticesDelaunay(uniqueVertices) {
      const activeObject = this.activeObject;

      // Verificar se existe um activeObject
      if (!activeObject) {
        console.error("No active object found.");
        return;
      }

      // Encontrar o mesh com userData.objectType igual a "gums" dentro do activeObject
      const gumsMesh = activeObject.children.find(
        (child) => child.userData.objectType === "gums"
      );
      if (!gumsMesh) {
        console.error("No gums mesh found in the active object.");
        return;
      }

      // Verificar se o gumsMesh tem geometria
      if (!gumsMesh.geometry) {
        console.error("No geometry found in the gums mesh.");
        return;
      }

      this.intersectionPoints = [];
      this.intermediatePoints = [];

      const cuttingPlane = this.scene.children.find(
        (child) => child.userData.isCuttingPlane
      );
      const negateNormal =
        activeObject.userData.modelType === "mandibular" ? true : false;
      const cuttingPlaneNormal = new THREE.Vector3(0, 0, 1); // Normal local do plano é Z+
      cuttingPlaneNormal.applyQuaternion(cuttingPlane.quaternion).normalize();
      const rayDirection = negateNormal
        ? cuttingPlaneNormal.clone().negate()
        : cuttingPlaneNormal.clone();

      for (let i = 0; i < uniqueVertices.length; i++) {
        const vertex = uniqueVertices[i];
        let raycaster = new THREE.Raycaster(vertex.clone(), rayDirection);

        let intersects = raycaster.intersectObject(cuttingPlane);
        if (intersects.length > 0) {
          const intersectionPoint = intersects[0].point;
          const scaleFactor = 1.0;
          const scaledPoint = intersectionPoint
            .clone()
            .multiplyScalar(scaleFactor);
          // Adicionando o ponto intermediário
          const midPoint = new THREE.Vector3().lerpVectors(
            vertex,
            scaledPoint,
            0.5
          );

          this.intermediatePoints.push(midPoint); // Adiciona o ponto intermediário
          this.intersectionPoints.push(scaledPoint); // Adiciona o ponto escalado como antes
        }
      }

      this.interIntersectionPoints = [];
      const maxPoints = 1000;

      // Encontrar outros meshes com o mesmo userData.modelType na cena, mas que não estejam dentro de activeObject
      const modelType = this.activeObject.userData.modelType;
      const otherMeshes = this.scene.children.filter(
        (child) =>
          child !== this.activeObject && // Certifique-se de não incluir o próprio activeObject
          child.userData.modelType === modelType && // Mesmo modelType
          child.geometry // Verifique se o mesh tem geometria
      );

      // Iterar sobre todos os otherMeshes encontrados
      otherMeshes.forEach((mesh) => {
        const positionAttribute = mesh.geometry.getAttribute("position");

        // Criar um array com os índices dos vértices
        const vertexIndices = Array.from(
          { length: positionAttribute.count },
          (_, i) => i
        );

        // Embaralhar os índices para selecionar um subconjunto aleatório
        for (let i = vertexIndices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [vertexIndices[i], vertexIndices[j]] = [
            vertexIndices[j],
            vertexIndices[i],
          ];
        }

        // Iterar sobre um número máximo de pontos aleatórios (até maxPoints)
        const selectedIndices = vertexIndices.slice(0, maxPoints);
        for (let i = 0; i < selectedIndices.length; i++) {
          const index = selectedIndices[i];
          const vertex = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            index
          );

          // Disparar o raio a partir do vértice na direção do plano de corte
          const raycaster = new THREE.Raycaster(
            vertex.clone(),
            rayDirection.clone().normalize()
          );
          const intersects = raycaster.intersectObject(cuttingPlane);

          if (intersects.length > 0) {
            // Obter o ponto de interseção
            const intersectionPoint = intersects[0].point;

            // Escalar o ponto de interseção, se necessário
            const scaleFactor = 1.0 + Math.random() * 0;
            const scaledPoint = intersectionPoint
              .clone()
              .multiplyScalar(scaleFactor);

            // Adicionar o ponto de interseção à nuvem de pontos
            this.interIntersectionPoints.push(scaledPoint);

            // Se já atingiu o número máximo de pontos, pare
            if (this.interIntersectionPoints.length >= maxPoints) {
              break;
            }
          }
        }
      });

      // Verificar se foram encontrados pontos de interseção
      if (this.interIntersectionPoints.length === 0) {
        console.warn("No intersection points were found.");
      } else {
        console.log(
          "Intersection points generated:",
          this.interIntersectionPoints.length
        );
      }

      const combinedVertices = [
        ...this.intersectionPoints,
        ...this.intermediatePoints,
        ...uniqueVertices,
      ];
      const newGeometry = this.delaunayTriangulation([
        ...this.intersectionPoints,
        ...this.interIntersectionPoints,
      ]);
      this.removeLargeTrianglesRaycaster(newGeometry, 15, gumsMesh);

      const newGeometryWalls = this.delaunayTriangulation(combinedVertices);
      this.removeLargeTrianglesRaycaster(newGeometryWalls, 15, gumsMesh);

      const originalGeometry = BufferGeometryUtils.mergeGeometries([
        gumsMesh.geometry,
      ]);

      if (newGeometry) {
        const deIndexedGeometry = this.deindexGeometry(newGeometry);
        deIndexedGeometry.computeVertexNormals();

        const deIndexedGeometryWall = this.deindexGeometry(newGeometryWalls);
        deIndexedGeometryWall.computeVertexNormals();

        const deIndexedOriginalGeometry =
          this.deindexGeometry(originalGeometry);
        deIndexedOriginalGeometry.computeVertexNormals();

        console.table({
          deIndexedGeometry,
          deIndexedGeometryWall,
          deIndexedOriginalGeometry,
        });

        const geometries = [
          deIndexedOriginalGeometry,
          deIndexedGeometryWall,
          deIndexedGeometry,
        ];
        const mergedGeometry = BufferGeometryUtils.mergeGeometries(
          geometries,
          false
        );

        if (mergedGeometry) {
          gumsMesh.geometry.dispose();
          gumsMesh.material.dispose();

          gumsMesh.geometry = mergedGeometry;
          gumsMesh.geometry.computeVertexNormals();

          gumsMesh.material = new THREE.MeshPhongMaterial({
            color: 0xffd0db, // 0xffc0cb
            specular: 0x333366,
            emissive: 0x000000,
            shininess: 0,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 1,
            reflectivity: 0,
          });

          this.calculateVertexNormals(gumsMesh.geometry);
          gumsMesh.updateMatrix();
          gumsMesh.material.needsUpdate = true;
        } else {
          console.error("Falha ao mesclar as geometrias.");
        }
      } else {
        console.error(
          "Falha ao criar a costura 3D com a triangulação de Delaunay."
        );
      }
    },
    identifyBorderVerticesDelaunay_(uniqueVertices) {
      const activeObject = this.activeObject;
      if (activeObject && activeObject.geometry) {
        this.intersectionPoints = [];

        const cuttingPlane = this.scene.children.find(
          (child) => child.userData.isCuttingPlane
        );
        const negateNormal =
          activeObject.userData.modelType === "mandibular" ? true : false;

        for (let i = 0; i < uniqueVertices.length; i++) {
          const vertex = uniqueVertices[i];
          const rayDirection = negateNormal
            ? cuttingPlane.userData.normal.clone().negate().normalize()
            : cuttingPlane.userData.normal.clone().normalize();
          let raycaster = new THREE.Raycaster(vertex.clone(), rayDirection);

          let intersects = raycaster.intersectObject(cuttingPlane);
          if (intersects.length > 0) {
            const intersectionPoint = intersects[0].point;
            // Definir o fator de escala
            const scaleFactor = 1.05;
            // Escalar o ponto de interseção
            const scaledPoint = intersectionPoint
              .clone()
              .multiplyScalar(scaleFactor);
            // Adicionar o ponto escalado ao array
            this.intersectionPoints.push(scaledPoint);
          }
        }

        // Combina os vértices únicos e os pontos de interseção em um único array
        // eslint-disable-next-line no-unused-vars
        const combinedVertices = [
          ...this.intersectionPoints,
          ...uniqueVertices,
        ];

        // Utiliza a função genérica de triangulação de Delaunay para gerar a nova geometria
        const newGeometry = this.delaunayTriangulation(this.intersectionPoints);
        this.removeLargeTrianglesRaycaster(newGeometry, 15, activeObject);

        const newGeometryWalls = this.delaunayTriangulation(combinedVertices);
        this.removeLargeTrianglesRaycaster(newGeometryWalls, 15, activeObject);

        const originalGeometry = BufferGeometryUtils.mergeGeometries([
          activeObject.geometry,
        ]);

        if (newGeometry) {
          const deIndexedGeometry = this.deindexGeometry(newGeometry);
          deIndexedGeometry.computeVertexNormals();

          const deIndexedGeometryWall = this.deindexGeometry(newGeometryWalls);
          deIndexedGeometryWall.computeVertexNormals();

          const deIndexedOriginalGeometry =
            this.deindexGeometry(originalGeometry);
          deIndexedOriginalGeometry.computeVertexNormals();

          // console.table({ deIndexedGeometry, deIndexedGeometryWall, deIndexedOriginalGeometry })

          // Utiliza BufferGeometryUtils para mesclar a geometria existente com a nova geometria
          const geometries = [
            deIndexedOriginalGeometry,
            deIndexedGeometryWall,
            deIndexedGeometry,
          ];
          const mergedGeometry = BufferGeometryUtils.mergeGeometries(
            geometries,
            false
          );

          if (mergedGeometry) {
            activeObject.geometry.dispose(); // Libera a memória da geometria antiga
            activeObject.material.dispose();

            // const iterations = 2;

            //     const params = {
            //       split: true, // optional, default: true
            //       uvSmooth: false, // optional, default: false
            //       preserveEdges: false, // optional, default: false
            //       flatOnly: false, // optional, default: false
            //       maxTriangles: 50000, // optional, default: Infinity
            //     };

            //     activeObject.geometry  = LoopSubdivision.modify(
            //         mergedGeometry,
            //       iterations,
            //       params
            //     );

            activeObject.geometry = mergedGeometry;
            activeObject.geometry.computeVertexNormals(); // Recalcula as normais para iluminação correta

            activeObject.material = new THREE.MeshPhongMaterial({
              color: 0x02bebe,
              specular: 0x000000,
              emissive: 0x000000,
              shininess: 0,
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 1,
            });
            this.calculateVertexNormals(activeObject.geometry);
            activeObject.updateMatrix();
            activeObject.material.needsUpdate = true;
          } else {
            console.error("Falha ao mesclar as geometrias.");
          }
        } else {
          console.error(
            "Falha ao criar a costura 3D com a triangulação de Delaunay."
          );
        }
      }
    },
    identifyBorderVertices() {
      const activeObject = this.activeObject;
      if (activeObject && activeObject.geometry) {
        const edgeVertices = [];

        const positionAttribute = this.smoothingMeshEdges.attributes.position;
        for (let i = 0; i < positionAttribute.count; i++) {
          const vertex = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            i
          );
          edgeVertices.push(vertex);
        }

        const uniqueVertices = this.uniqueVerticesFunction(edgeVertices);
        this.smoothGeometryVertices = [];
        this.intersectionPoints = [];

        const negateNormal =
          activeObject.userData.modelType === "mandibular" ? true : false;

        // Certifique-se de que a normal está correta e apontando para fora da borda
        const cuttingPlane = this.scene.children.find(
          (child) => child.userData.isCuttingPlane
        );
        for (let i = 0; i < uniqueVertices.length; i++) {
          const vertex = uniqueVertices[i];
          const nextVertex =
            uniqueVertices[
              this.findClosestVertex(i, vertex.clone(), uniqueVertices)
            ];

          if (nextVertex !== undefined) {
            // Crie um raio na direção oposta à normal do plano de corte para o vertex atual
            const rayDirection = negateNormal
              ? cuttingPlane.userData.normal.clone().negate().normalize()
              : cuttingPlane.userData.normal.clone().normalize();
            let raycaster = new THREE.Raycaster(vertex.clone(), rayDirection);

            // Intersecta o raio com o plano de corte para o vertex atual
            let intersects = raycaster.intersectObject(cuttingPlane);

            if (intersects.length > 0) {
              const intersectionPoint = intersects[0].point;

              // Armazena o ponto de interseção
              this.intersectionPoints.push(intersectionPoint);

              // Repita o processo para o nextVertex
              raycaster.set(nextVertex.clone(), rayDirection);

              intersects = raycaster.intersectObject(cuttingPlane);

              if (intersects.length > 0) {
                const nextIntersectionPoint = intersects[0].point;

                // Armazena o ponto de interseção
                this.intersectionPoints.push(intersectionPoint);

                // Primeiro triângulo: vertex -> intersectionPoint -> nextIntersectionPoint
                const v1 = vertex;
                const v2 = intersectionPoint;
                const v3 = nextIntersectionPoint;
                this.addTriangleToSmoothMesh(v1, v2, v3);

                // Segundo triângulo: nextIntersectionPoint -> nextVertex -> vertex
                // Este triângulo preenche o espaço entre os vértices e suas projeções no plano
                const v4 = nextIntersectionPoint;
                const v5 = nextVertex.clone();
                const v6 = vertex.clone();
                this.addTriangleToSmoothMesh(v4, v5, v6);
              }
            }
          }
        }
        this.updateActiveObject();
      }
    },
    getPointOnModel(event, ignoreObject = null) {
      const mouse = new THREE.Vector2();
      const rect = this.renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.camera);

      // Filtra apenas objetos visíveis e, se fornecido, ignora o objeto especificado (a esfera sendo arrastada)
      let objects = this.scene.children.filter((child) => child.visible);

      if (ignoreObject) {
        const ignoreUUID = ignoreObject.uuid;
        objects = objects.filter(
          (child) =>
            (child.type !== "Group" || child.userData.modelType) &&
            child.uuid !== ignoreUUID
        );
        //objects = objects.filter(child => child.uuid !== ignoreUUID);
      }

      const intersects = raycaster.intersectObjects(objects, true);

      if (intersects.length > 0) {
        if (event.ctrlKey) {
          this.removeTriangle(intersects[0].object, intersects[0].faceIndex);
        }
        // Retorna o ponto mais próximo da câmera que não é o objeto ignorado
        for (let i = 0; i < intersects.length; i++) {
          if (intersects[i].object !== ignoreObject) {
            return {
              point: intersects[i].point,
              objectRef: intersects[i].object,
              intersects: intersects[0].faceIndex,
            };
          }
        }
      }

      return null;
    },
    applyPressure(point, toothPosition, mesh) {
      const vertices = mesh.geometry.attributes.position.array;
      const influenceRadius = 5;
      const forceMagnitude = 1;

      for (let i = 0; i < vertices.length; i += 3) {
        const vertex = new THREE.Vector3(
          vertices[i],
          vertices[i + 1],
          vertices[i + 2]
        );
        const distance = vertex.distanceTo(point);

        if (distance <= influenceRadius) {
          const forceFactor = 1 - distance / influenceRadius;
          const forceDirection = vertex.clone().sub(toothPosition).normalize();

          // Acumular forças para suavidade
          vertices[i] += forceDirection.x * forceMagnitude * forceFactor;
          vertices[i + 1] += forceDirection.y * forceMagnitude * forceFactor;
          vertices[i + 2] += forceDirection.z * forceMagnitude * forceFactor;
        }
      }
      mesh.geometry.attributes.position.needsUpdate = true;
    },
    applyPhysics(teeth, mesh) {
      const animate = () => {
        teeth.forEach((tooth) => {
          // Certifique-se de que o BVH foi gerado para ambos os objetos
          if (!mesh.geometry.boundsTree) {
            mesh.geometry.computeBoundsTree();
          }
          if (!tooth.geometry.boundsTree) {
            tooth.geometry.computeBoundsTree();
          }

          // Atualizar transformações globais
          tooth.updateMatrixWorld(true);
          mesh.updateMatrixWorld(true);

          // Obter bounding box global do tooth
          const toothBoundingBox = new THREE.Box3().setFromObject(tooth);

          // Usar shapecast no mesh
          mesh.geometry.boundsTree.shapecast({
            intersectsBounds: (box) => {
              // Checar interseção do bounding box do dente com o atual
              const intersects = box.intersectsBox(toothBoundingBox);
              return intersects; // Retorna true se intersectar
            },

            intersectsTriangle: (tri) => {
              // Aqui você pode processar os triângulos que intersectam
              const triangle = new THREE.Triangle(
                new THREE.Vector3(tri.a.x, tri.a.y, tri.a.z),
                new THREE.Vector3(tri.b.x, tri.b.y, tri.b.z),
                new THREE.Vector3(tri.c.x, tri.c.y, tri.c.z)
              );

              const triPoint = new THREE.Vector3();
              triangle.closestPointToPoint(
                toothBoundingBox.getCenter(new THREE.Vector3()),
                triPoint
              );

              // Verifica se o triângulo está suficientemente próximo do bounding box
              const isInside = toothBoundingBox.containsPoint(triPoint);
              if (isInside && this.physicsMode) {
                this.applyPressure(triPoint, tooth.position, mesh);
              }
              return isInside;
            },
          });
        });
        requestAnimationFrame(animate);
      };

      animate();
    },
    initSimplePhysics(mesh) {
      const vertices = mesh.geometry.attributes.position.array;
      const originalPositions = new Float32Array(vertices); // Salvar a posição original dos vértices
      const forces = new Float32Array(vertices.length); // Iniciar forças zeradas
      const damping = 0.5; // Amortecimento para suavizar movimentos
      const forceDecay = 0.5; // Redução gradual da força
      const returnSpeed = 0.01; // Velocidade de retorno à posição original
      const maxDisplacement = 4; // Limite máximo de deslocamento

      // Atualizar vértices com base nas forças
      const updateVertices = () => {
        for (let i = 0; i < vertices.length; i += 3) {
          const force = new THREE.Vector3(
            forces[i],
            forces[i + 1],
            forces[i + 2]
          );
          force.multiplyScalar(damping); // Amortecimento da força

          // Atualizar posição com base nas forças
          vertices[i] += force.x;
          vertices[i + 1] += force.y;
          vertices[i + 2] += force.z;

          // Gradualmente trazer o vértice de volta à posição original
          const original = new THREE.Vector3(
            originalPositions[i],
            originalPositions[i + 1],
            originalPositions[i + 2]
          );
          const current = new THREE.Vector3(
            vertices[i],
            vertices[i + 1],
            vertices[i + 2]
          );
          const toOriginal = original.sub(current).multiplyScalar(returnSpeed);

          vertices[i] += toOriginal.x;
          vertices[i + 1] += toOriginal.y;
          vertices[i + 2] += toOriginal.z;

          // Limitar deslocamento em relação à posição original
          const displacement = new THREE.Vector3(
            vertices[i] - originalPositions[i],
            vertices[i + 1] - originalPositions[i + 1],
            vertices[i + 2] - originalPositions[i + 2]
          );

          if (displacement.length() > maxDisplacement) {
            displacement.clampLength(0, maxDisplacement);
            vertices[i] = originalPositions[i] + displacement.x;
            vertices[i + 1] = originalPositions[i + 1] + displacement.y;
            vertices[i + 2] = originalPositions[i + 2] + displacement.z;
          }

          // Gradualmente reduzir forças
          forces[i] *= forceDecay;
          forces[i + 1] *= forceDecay;
          forces[i + 2] *= forceDecay;
        }

        mesh.geometry.attributes.position.needsUpdate = true;
      };

      // Loop de física
      const physicsLoop = () => {
        updateVertices();
        requestAnimationFrame(physicsLoop);
      };

      physicsLoop();
    },
    findGumInScene() {
      let foundGum = null;

      // Função auxiliar para buscar recursivamente a gengiva dentro de grupos
      function searchGum(object) {
        if (object instanceof THREE.Group) {
          object.traverse((child) => {
            if (child.userData.objectType === "gums") {
              foundGum = child;
            }
          });
        }
      }

      // Função auxiliar para buscar pela maxila ou mandíbula caso a gengiva não seja encontrada
      function searchMaxillaryOrMandibular(object) {
        if (
          object.userData.modelType === "maxillary" ||
          object.userData.modelType === "mandibular"
        ) {
          foundGum = object;
        }
      }

      // Busca inicial no nível superior da cena
      this.scene.children.forEach((child) => {
        if (child instanceof THREE.Group) {
          searchGum(child);
        } else if (child.userData.objectType === "gums") {
          // Caso a gengiva não esteja dentro de um grupo, mas diretamente na cena
          foundGum = child;
        }
      });

      // Se não encontrar a gengiva, procura pela maxila ou mandíbula
      if (!foundGum) {
        this.scene.traverse((child) => {
          searchMaxillaryOrMandibular(child);
        });
      }

      return foundGum;
    },
    findGumsInScene() {
      let foundGums = []; // Lista para acumular gengivas encontradas
      let alternativeObjects = []; // Lista para maxila ou mandíbula, se não encontrar gengivas

      // Função auxiliar para buscar recursivamente a gengiva dentro de grupos
      function searchGum(object) {
        if (object instanceof THREE.Group) {
          object.traverse((child) => {
            if (child.userData.objectType === "gums") {
              child.userData.modelType = object.userData.modelType;
              foundGums.push(child); // Adiciona à lista de gengivas encontradas
            }
          });
        }
      }

      // Função auxiliar para buscar pela maxila ou mandíbula
      function searchMaxillaryOrMandibular(object) {
        if (
          object.userData.modelType === "maxillary" ||
          object.userData.modelType === "mandibular"
        ) {
          alternativeObjects.push(object); // Adiciona à lista alternativa
        }
      }

      // Busca inicial no nível superior da cena
      this.scene.children.forEach((child) => {
        if (child instanceof THREE.Group) {
          searchGum(child);
        } else if (child.userData.objectType === "gums") {
          foundGums.push(child); // Adiciona diretamente à lista de gengivas encontradas
        }
      });

      // Se não encontrar gengivas, procura pela maxila ou mandíbula
      if (foundGums.length === 0) {
        this.scene.traverse((child) => {
          searchMaxillaryOrMandibular(child);
        });
      }

      // Retorna as gengivas encontradas ou, se não houver, os objetos alternativos
      return foundGums.length > 0 ? foundGums : alternativeObjects;
    },
    findDynamicGumInScene() {
      let foundDynamicGum = null;

      // Função auxiliar para buscar recursivamente a gengiva dentro de grupos
      function searchGum(object) {
        if (object instanceof THREE.Group) {
          object.traverse((child) => {
            if (child.userData.objectType === "dynamic") {
              foundDynamicGum = child;
            }
          });
        }
      }

      // Busca inicial no nível superior da cena
      this.scene.children.forEach((child) => {
        if (child instanceof THREE.Group) {
          searchGum(child);
        } else if (child.userData.objectType === "dynamic") {
          // Caso a gengiva não esteja dentro de um grupo, mas diretamente na cena
          foundDynamicGum = child;
        }
      });

      return foundDynamicGum;
    },
    storeOriginalVertices(gumGeometry) {
      const gumVertices = gumGeometry.attributes.position;
      const originalVertices = [];

      for (let i = 0; i < gumVertices.count; i++) {
        const vertex = new THREE.Vector3().fromBufferAttribute(gumVertices, i);
        originalVertices.push(vertex);
      }

      return originalVertices;
    },
    deformGumToFollowTooth(gumGeometry, toothMesh, originalVertices) {
      const gumVertices = gumGeometry.attributes.position;
      const toothSphereRadius = 0.5; // Ajuste conforme necessário
      const toothPosition = toothMesh.position.clone();
      const vertex = new THREE.Vector3();
      const waveAmplitude = 0.1; // Ajuste a amplitude da "onda"
      const waveSteepness = 10; // Ajuste a íngreme da "onda"

      for (let i = 0; i < gumVertices.count; i++) {
        vertex.fromBufferAttribute(gumVertices, i);
        const originalVertex = originalVertices[i];
        const distanceToTooth = vertex.distanceTo(toothPosition);

        if (distanceToTooth < toothSphereRadius) {
          // Calcular um fator de deformação baseado na distância
          const scalarField = 1 - distanceToTooth / toothSphereRadius;
          const deformation =
            Math.exp(-waveSteepness * scalarField) * waveAmplitude;

          // Direção da deformação
          const direction = toothPosition.clone().add(vertex).normalize();
          direction.multiplyScalar(deformation);

          // Elevação adicional para criar o efeito de "onda"
          direction.z += Math.sin(scalarField * Math.PI) * deformation;

          vertex.add(direction);
          gumVertices.setXYZ(i, vertex.x, vertex.y, vertex.z);
        } else {
          // Interpolar de volta para a posição original se o dente estiver longe
          vertex.lerp(originalVertex, 0.1); // Ajuste o fator de interpolação conforme necessário
          gumVertices.setXYZ(i, vertex.x, vertex.y, vertex.z);
        }
      }

      gumVertices.needsUpdate = true;
      gumGeometry.computeVertexNormals();
    },
    updateCameraFocusBasedOnDentalArches() {
      // Reinicia a bounding box para cada chamada
      let combinedBBox = new THREE.Box3();

      // Percorre todos os objetos na cena
      this.scene.traverse((object) => {
        // Verifica se o objeto é um dos modelos dentários de interesse
        if (
          object.userData.modelType === "maxillary" ||
          object.userData.modelType === "mandibular"
        ) {
          // Se sim, atualiza a bounding box combinada para incluir este objeto
          combinedBBox.union(new THREE.Box3().setFromObject(object));
        }
      });

      // Calcula o centro da bounding box combinada
      let center = new THREE.Vector3();
      combinedBBox.getCenter(center);

      // Verifica se o centro realmente mudou antes de atualizar
      if (!this.controls.target.equals(center)) {
        this.controls.target.copy(center);
        this.controls.update();
      }
    },
    calculateSceneBoundingBox() {
      const box = new THREE.Box3();
      box.setFromObject(this.activeObject); // 'this.scene' é o objeto THREE.Scene

      return box;
    },
    initializeOctreeWithTeeth() {
      const sceneBoundingBox = this.calculateSceneBoundingBox();
      const octreeBoundary = new Box(
        (sceneBoundingBox.min.x + sceneBoundingBox.max.x) / 2,
        (sceneBoundingBox.min.y + sceneBoundingBox.max.y) / 2,
        (sceneBoundingBox.min.z + sceneBoundingBox.max.z) / 2,
        Math.max(
          sceneBoundingBox.max.x - sceneBoundingBox.min.x,
          sceneBoundingBox.max.y - sceneBoundingBox.min.y,
          sceneBoundingBox.max.z - sceneBoundingBox.min.z
        ) / 2
      );

      this.octree = new Octree(octreeBoundary, 2);

      const teeth = this.findTeethInScene();

      teeth.forEach((tooth) => {
        const toothGeometry = tooth.geometry;

        if (toothGeometry instanceof THREE.BufferGeometry) {
          const positions = toothGeometry.attributes.position;
          const vertices = [];

          for (let i = 0; i < positions.count; i++) {
            const position = new THREE.Vector3().fromBufferAttribute(
              positions,
              i
            );
            // Ajuste a posição com a posição do objeto "dente"
            const realPosition = position.applyMatrix4(tooth.matrixWorld);
            vertices.push(realPosition);
          }

          const boundingBox = computeBoundingBox(vertices);

          const octreeObject = {
            boundary: boundingBox,
            vertices: vertices,
            position: tooth.position.clone(), // Use a posição do objeto "dente"
            userData: tooth.userData,
          };

          this.octree.insert(octreeObject);
        }
      });
      console.table(this.octree);
    },
    animate() {
      // Salva o ID do requestAnimationFrame para poder cancelar no futuro
      this.frameId = window.requestAnimationFrame(this.animate);

      //const deltaTime = this.clock.getDelta(); // Certifique-se de criar um THREE.Clock no seu data
      //this.updatePhysics(deltaTime);
      //this.composer.render(this.scene, this.camera);
      //this.updateGumSimulation();
      //this.displaceBothGumsAlongNormals();
      this.renderer.render(this.scene, this.camera);
    },
    stopAnimate() {
      if (this.frameId) {
        window.cancelAnimationFrame(this.frameId);
      }
    },
    processTask(task) {
      switch (task.action) {
        case "loadModel":
          this.$refs.progressDialog.showProgressDialog(
            "Carregando e Preparando STL. Aguarde...",
            10
          );
          setTimeout(() => {
            this.loadModel(task.params);
          }, 1000);
          break;
        case "createOcclusionPlan":
          this.createOcclusionPlan(task.params);
          this.$store.dispatch("editor3D/endOcclusionPlanCreation");
          break;
        case "createDentalArch":
          this.newCreatingDentalArch(task.params);
          break;
      }
      this.$store.dispatch("editor3D/executeTask", task.id);
    },
  },
};
